// Dates and times in UTC (APS-412, admin-d.md "Dates and months"): mono text fields,
// `AAAA-MM-DD HH:MM` (or `AAAA-MM-DD` for a date alone), "UTC" in the label when there is a
// time, checked when a field loses focus. Never a native date or datetime-local input, which
// shows the browser's language and the device's zone. UtcField is one value (an expiry, an
// "as of" date); UtcRange is two, with the end after the start.
import { useId, useState } from 'react'
import { type MessageId, useI18n } from '../../i18n'

const DATE_TIME = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * `2026-10-14 18:30` → its ISO instant in UTC; with `time: false`, `2026-10-14` → `2026-10-14`.
 * Null when it is not a real date (and time).
 */
export function parseUtc(text: string, { time = true }: { time?: boolean } = {}): string | null {
  const m = (time ? DATE_TIME : DATE).exec(text.trim())
  if (!m) return null
  const [y, mo, d, h = 0, mi = 0] = m.slice(1).map(Number) as number[]
  if (y === undefined || mo === undefined || d === undefined) return null
  const at = new Date(Date.UTC(y, mo - 1, d, h, mi))
  // Date.UTC rolls 2026-02-30 over to March: a real date reads back the same.
  if (
    at.getUTCFullYear() !== y ||
    at.getUTCMonth() !== mo - 1 ||
    at.getUTCDate() !== d ||
    at.getUTCHours() !== h ||
    at.getUTCMinutes() !== mi
  )
    return null
  return time ? at.toISOString() : at.toISOString().slice(0, 10)
}

/** An ISO instant or date → the fields' own shape, in UTC. */
export function formatUtc(iso: string, { time = true }: { time?: boolean } = {}): string {
  return time ? iso.slice(0, 16).replace('T', ' ') : iso.slice(0, 10)
}

/**
 * One date (and time) in UTC. `value` is what is typed; `onChange` gets it as typed and as its
 * ISO value (null while it is not a real date). The field says what is wrong once it is left;
 * `error` adds a message of the form's own (a range's order).
 */
export function UtcField({
  label,
  value,
  onChange,
  time = true,
  required,
  error,
  name,
}: {
  /** The field's name; " (UTC)" follows when it has a time. */
  label: string
  value: string
  onChange: (text: string, iso: string | null) => void
  time?: boolean | undefined
  required?: boolean | undefined
  error?: MessageId | undefined
  name?: string | undefined
}) {
  const { t } = useI18n()
  const ids = { field: useId(), err: useId() }
  const [left, setLeft] = useState(false)
  const shape = left && value.trim() !== '' && parseUtc(value, { time }) === null
  const message: MessageId | undefined = shape
    ? time
      ? 'kit.range.invalid'
      : 'kit.date.invalid'
    : error
  return (
    <div className="field-d">
      <label className="lbl" htmlFor={ids.field}>
        {time ? t('kit.utc.label', { label }) : label}
      </label>
      <input
        id={ids.field}
        name={name}
        className="input mono"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        placeholder={t(time ? 'kit.range.placeholder' : 'kit.date.placeholder')}
        required={required}
        value={value}
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? ids.err : undefined}
        onChange={(e) => onChange(e.target.value, parseUtc(e.target.value, { time }))}
        onBlur={() => setLeft(true)}
      />
      {message ? (
        <p className="field-err" id={ids.err} role="alert">
          {t(message)}
        </p>
      ) : null}
    </div>
  )
}

export interface UtcRangeValue {
  from: string
  to: string
}

/**
 * Two UtcFields, De and Até. A form reads the instants with parseUtc and submits only when
 * `utcRangeError(value)` is null.
 */
export function UtcRange({
  value,
  onChange,
  required,
}: {
  value: UtcRangeValue
  onChange: (value: UtcRangeValue) => void
  required?: boolean | undefined
}) {
  const { t } = useI18n()
  const order = utcRangeError(value, { from: false, to: false })
  return (
    <fieldset className="utc-range">
      <legend className="visually-hidden">{t('kit.range.legend')}</legend>
      <UtcField
        name="from"
        label={t('kit.range.from')}
        value={value.from}
        required={required}
        onChange={(from) => onChange({ ...value, from })}
      />
      <UtcField
        name="to"
        label={t('kit.range.to')}
        value={value.to}
        required={required}
        error={order?.message}
        onChange={(to) => onChange({ ...value, to })}
      />
    </fieldset>
  )
}

/**
 * What is wrong with the range, if anything: a field in the wrong shape (once it was left), or
 * an end that is not after the start. Empty fields are the form's `required` to refuse.
 */
export function utcRangeError(
  value: UtcRangeValue,
  touched: { from: boolean; to: boolean } = { from: true, to: true },
): { field: 'from' | 'to' | 'both'; message: 'kit.range.invalid' | 'kit.range.order' } | null {
  const from = value.from.trim() ? parseUtc(value.from) : undefined
  const to = value.to.trim() ? parseUtc(value.to) : undefined
  if (touched.from && from === null) return { field: 'from', message: 'kit.range.invalid' }
  if (touched.to && to === null) return { field: 'to', message: 'kit.range.invalid' }
  if (from && to && to <= from) return { field: 'both', message: 'kit.range.order' }
  return null
}
